import { uploadBlob } from '../api/blobUpload';
import { sedrApi } from '../api/sedr';
import {
  buildGuestPasswordVerifier,
  createGuestPasswordSalt,
  deriveGuestPasswordKey,
  encodePasswordVerifierBase64,
} from './guestPassword';
import { deriveProofKeys } from './infoShareFragment';
import {
  decryptAesGcm,
  decodeBase64Url,
  encodeBase64Url,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  unpackEncryptedBlob,
} from './primitives';
import {
  buildSedrGuestFragment,
  type SedrGuestFragment,
} from './sedrGuestFragment';
import { resolvePasswordPolicy } from './passwordPoliciesApi';
import { assertPasswordPolicy } from './passwordPolicy';

export const SEDR_GUEST_ENVELOPE_AAD = 'shareweb:sedr-guest-envelope:v1';
const GUEST_WRAP_HKDF_INFO = 'shareweb:guest-wrap:v1';

export async function encryptSedrGuestEnvelope(
  roomKey: Uint8Array,
  guestWrapKey: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(guestWrapKey);
  const encrypted = await encryptAesGcm(
    aesKey,
    roomKey,
    new TextEncoder().encode(SEDR_GUEST_ENVELOPE_AAD),
  );
  return packEncryptedBlob(encrypted);
}

export async function decryptSedrGuestEnvelope(
  envelopeBlob: Uint8Array,
  guestWrapKey: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(guestWrapKey);
  return decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(envelopeBlob),
    new TextEncoder().encode(SEDR_GUEST_ENVELOPE_AAD),
  );
}

export async function deriveSedrGuestWrapKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const unwrapKey = await deriveGuestPasswordKey(password, salt);
  return hkdfSha256(unwrapKey, new Uint8Array(0), GUEST_WRAP_HKDF_INFO, 32);
}

export async function resolveSedrGuestWrapKey(
  fragment: SedrGuestFragment,
  password?: string,
): Promise<Uint8Array | null> {
  if (fragment.gw) {
    return decodeBase64Url(fragment.gw);
  }
  if (!fragment.salt || !password) {
    return null;
  }
  return deriveSedrGuestWrapKeyFromPassword(password, decodeBase64Url(fragment.salt));
}

export async function createSedrGuestInvite(input: {
  roomId: string;
  roomKey: Uint8Array;
  displayName: string;
  organization: string;
  email?: string;
  password?: string;
  expiresAt?: string | null;
  guestRole?: string;
  allowCommenting?: boolean;
}): Promise<{ guestUrl: string; guestInviteId: string }> {
  const proofSeed = randomBytes(32);
  const { publicKey: proofPublicKey } = deriveProofKeys(proofSeed);

  const password = input.password?.trim() ?? '';
  const usePassword = password.length > 0;
  let guestWrapKey: Uint8Array;
  let passwordVerifier: string | undefined;
  let passwordMode = 'None';
  let fragmentSalt: string | undefined;

  if (usePassword) {
    const policy = await resolvePasswordPolicy('sedrGuestPassword');
    assertPasswordPolicy(password, policy);
    const salt = createGuestPasswordSalt();
    guestWrapKey = await deriveSedrGuestWrapKeyFromPassword(password, salt);
    passwordVerifier = encodePasswordVerifierBase64(
      await buildGuestPasswordVerifier(password, salt),
    );
    passwordMode = 'UserSet';
    fragmentSalt = encodeBase64Url(salt);
  } else {
    guestWrapKey = randomBytes(32);
  }

  const envelopeBlob = await encryptSedrGuestEnvelope(input.roomKey, guestWrapKey);
  const envelopeBlobId = await uploadBlob(envelopeBlob);

  const created = await sedrApi.createGuestInvite(input.roomId, {
    displayName: input.displayName,
    organization: input.organization,
    email: input.email || null,
    envelopeBlobId,
    fragmentProofPublicKey: encodeBase64Url(proofPublicKey),
    passwordMode,
    passwordVerifier,
    expiresAt: input.expiresAt ?? null,
    guestRole: input.guestRole ?? 'GuestReader',
    allowCommenting: Boolean(input.allowCommenting),
    accessMode: 'LegacyFragment',
  });

  if (!created.guestPath || !created.guestInviteId) {
    throw new Error('unexpected_invite_outcome');
  }

  const fragmentPayload: SedrGuestFragment = {
    v: 1,
    ps: encodeBase64Url(proofSeed),
    ...(usePassword ? { salt: fragmentSalt } : { gw: encodeBase64Url(guestWrapKey) }),
  };

  const fragment = buildSedrGuestFragment(fragmentPayload);
  const guestUrl = `${window.location.origin}${created.guestPath}#${fragment}`;
  return { guestUrl, guestInviteId: created.guestInviteId };
}

export async function fetchAndDecryptSedrGuestRoomKey(
  roomToken: string,
  guestInviteId: string,
  fragment: SedrGuestFragment,
  password?: string,
): Promise<Uint8Array> {
  const wrapKey = await resolveSedrGuestWrapKey(fragment, password);
  if (!wrapKey) {
    throw new Error('guest_wrap_key_missing');
  }
  const { envelopeBlobId } = await sedrApi.guestKeyEnvelope(roomToken, guestInviteId);
  const response = await fetch(`/api/blobs/${envelopeBlobId}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('envelope_download_failed');
  }
  const envelope = new Uint8Array(await response.arrayBuffer());
  return decryptSedrGuestEnvelope(envelope, wrapKey);
}
